import React, { useRef, useState } from 'react';
import { Camera, Image, X, Check } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

export default function CameraCapture({ onCapture, onCancel, facing = 'user', label = 'Take Photo' }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [streaming, setStreaming] = useState(false);
  const [captured, setCaptured] = useState(null);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const startCamera = async () => {
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facing, width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
        setStreaming(true);
      }
    } catch (err) {
      setError('Camera not available. Tap to use camera capture.');
      // Fallback: trigger file input with camera capture
      if (fileInputRef.current) fileInputRef.current.click();
    }
  };

  const stopCamera = () => {
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(t => t.stop());
      videoRef.current.srcObject = null;
    }
    setStreaming(false);
  };

  const takePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const canvas = canvasRef.current;
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext('2d');
    if (facing === 'user') {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(videoRef.current, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
    setCaptured(dataUrl);
    stopCamera();
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setCaptured(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const confirm = () => {
    if (captured) onCapture(captured);
  };

  const retake = () => {
    setCaptured(null);
    startCamera();
  };

  const cancel = () => {
    stopCamera();
    setCaptured(null);
    if (onCancel) onCancel();
  };

  return (
    <div className="space-y-3">
      {/* Preview / Camera */}
      <div className="relative rounded-lg overflow-hidden" style={{ background: '#0A2540', aspectRatio: '4/3' }}>
        {captured ? (
          <img src={captured} alt="Captured" className="w-full h-full object-cover" />
        ) : streaming ? (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover"
            style={{ transform: facing === 'user' ? 'scaleX(-1)' : 'none' }}
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-3">
            <Camera size={48} weight="duotone" style={{ color: '#8A94A6' }} />
            <p className="text-sm text-white/60">{label}</p>
            {error && <p className="text-xs px-4 text-center" style={{ color: '#FF3B30' }}>{error}</p>}
          </div>
        )}
        <canvas ref={canvasRef} className="hidden" />
      </div>

      {/* Controls */}
      <div className="flex gap-2">
        {captured ? (
          <>
            <Button onClick={retake} variant="outline" className="flex-1 h-12 text-sm font-semibold rounded-lg" style={{ borderColor: '#E2E4E9' }}>
              <Camera size={18} className="mr-2" /> Retake
            </Button>
            <Button onClick={confirm} className="flex-1 h-12 text-sm font-semibold rounded-lg" style={{ background: '#34C759', color: 'white' }}>
              <Check size={18} className="mr-2" /> Confirm
            </Button>
          </>
        ) : streaming ? (
          <>
            <Button onClick={cancel} variant="outline" className="w-14 h-12 rounded-lg shrink-0" style={{ borderColor: '#E2E4E9' }}>
              <X size={18} />
            </Button>
            <Button onClick={takePhoto} className="flex-1 h-12 text-sm font-semibold rounded-lg" style={{ background: '#FF3B30', color: 'white' }}>
              <Camera size={18} className="mr-2" /> Capture
            </Button>
          </>
        ) : (
          <>
            <Button onClick={startCamera} className="flex-1 h-12 text-sm font-semibold rounded-lg" style={{ background: '#007AFF', color: 'white' }}>
              <Camera size={18} className="mr-2" /> Open Camera
            </Button>
            {/* Camera-only capture for mobile devices that can't use getUserMedia */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture={facing === 'user' ? 'user' : 'environment'}
              onChange={handleFileUpload}
              className="hidden"
            />
          </>
        )}}
      </div>
    </div>
  );
}
